import { useEffect, useMemo, useState } from "react";
import { masterApi, apiError, formatPhone } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { inicioDiaBR, fimDiaBR, hojeBR } from "@/lib/periodoBR";
import {
  LIMITE_DIAS, DIAS_SEMANA, normalizarDias, alternarDia, unirDias, diasDaSemanaNoMes,
  gradeDoMes, somaMes, nomeMes, mesDoDia, fmtDiaCurto, resumoDias,
} from "@/lib/sorteioDias";
import { Ticket, Trophy, Users, Building2, ChevronLeft, ChevronRight, X, XCircle, CalendarDays } from "lucide-react";

const fmtData = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const fmtDataHora = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");
const inputCls = "mt-1 h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";
const fmtCent = (v) => Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

// Item 2 (19/10): situação do prêmio de cada ganhador (pill no padrão da Troca de Óleo).
const STATUS_PREMIO = {
  disponivel: { label: "Disponível", cls: "chip-yellow" },
  usado: { label: "Usado", cls: "chip-green" },
  cancelado: { label: "Cancelado", cls: "chip-red" },
  expirado: { label: "Expirado", cls: "chip-gray" },
};

const VALIDADE_PADRAO = 30;
const VALIDADE_MAX = 365;

/** "período 01/10/2026 a 24/10/2026" ou "dias 04/10, 11/10" (resultado e histórico). */
function descricaoPeriodo(s) {
  if (s?.modo === "dias" && Array.isArray(s.dias) && s.dias.length) {
    return `${s.dias.length} dia${s.dias.length === 1 ? "" : "s"}: ${resumoDias(s.dias)}`;
  }
  return `${fmtData(s?.inicio)} a ${fmtData(s?.fim)}`;
}

export default function MasterSorteio() {
  const { unitId, unidades } = useUnit();
  const hoje = hojeBR();
  const [modo, setModo] = useState("periodo"); // periodo | dias
  const [inicio, setInicio] = useState(`${hoje.slice(0, 8)}01`);
  const [fim, setFim] = useState(hoje);
  const [dias, setDias] = useState([]);
  const [quantidade, setQuantidade] = useState(1);
  const [premio, setPremio] = useState("");          // centavos por litro (vazio = sem prêmio)
  const [validade, setValidade] = useState(String(VALIDADE_PADRAO));
  const [participantes, setParticipantes] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [sorteando, setSorteando] = useState(false);
  const [busy, setBusy] = useState(null); // id do prêmio com cancelamento em andamento

  const unitNome = unitId !== "all" ? (unidades?.find((u) => u.id === unitId)?.nome || "") : "";
  // Dia de Brasília (00:00 a 23:59:59.999999), independente do fuso do aparelho: lib/periodoBR.
  const isoIni = inicioDiaBR(inicio);
  const isoFim = fimDiaBR(fim);
  const periodoValido = !!(isoIni && isoFim && inicio <= fim);
  const diasOk = normalizarDias(dias);
  const selecaoValida = modo === "dias" ? diasOk.length > 0 && diasOk.length <= LIMITE_DIAS : periodoValido;
  const chaveDias = diasOk.join(",");

  const loadHistorico = async () => {
    try {
      const { data } = await masterApi.get("/master/sorteios");
      setHistorico(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { loadHistorico(); }, []);

  useEffect(() => {
    setParticipantes(null);
    if (!selecaoValida) return;
    const p = modo === "dias"
      ? new URLSearchParams({ dias: chaveDias })
      : new URLSearchParams({ inicio: isoIni, fim: isoFim });
    if (unitId !== "all") p.set("unit_id", unitId);
    // Cliques rápidos nos dias: resposta de uma seleção antiga não sobrescreve a atual.
    let vigente = true;
    masterApi.get(`/master/sorteios/participantes?${p.toString()}`)
      .then((r) => { if (vigente) setParticipantes(r.data); })
      .catch((e) => { if (vigente) modal.error(apiError(e)); });
    return () => { vigente = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, inicio, fim, chaveDias, unitId]);

  // Prêmio de cada ganhador: o histórico (recarregado após sortear/cancelar) é a fonte mais nova.
  const premioHist = useMemo(() => {
    const m = new Map();
    historico.forEach((s) => (s.ganhadores || []).forEach((g) => {
      if (g.premio !== undefined) m.set(`${s.id}|${g.cliente_id}`, g.premio);
    }));
    return m;
  }, [historico]);
  const premioDe = (s, g) => {
    const k = `${s?.id}|${g.cliente_id}`;
    return premioHist.has(k) ? premioHist.get(k) : (g.premio ?? null);
  };

  const lerPremio = () => {
    const txt = String(premio).trim().replace(",", ".");
    if (!txt) return { valor: null };
    const v = Number(txt);
    if (!Number.isFinite(v) || v <= 0) return { erro: "O prêmio deve ser maior que zero (ou deixe vazio para sorteio sem prêmio)." };
    return { valor: v };
  };
  const lerValidade = () => {
    const v = parseInt(validade, 10);
    if (!Number.isFinite(v) || v < 1 || v > VALIDADE_MAX) return { erro: `A validade do prêmio deve ser de 1 a ${VALIDADE_MAX} dias.` };
    return { valor: v };
  };

  const sortear = async () => {
    if (modo === "dias") {
      if (!diasOk.length) return modal.error("Marque pelo menos um dia.");
      if (diasOk.length > LIMITE_DIAS) return modal.error(`O sorteio aceita no máximo ${LIMITE_DIAS} dias marcados.`);
    } else if (!periodoValido) {
      return modal.error("Informe um período válido (início antes do fim).");
    }
    const qtd = Math.max(1, parseInt(quantidade) || 1);
    if (qtd > 50) return modal.error("O sorteio permite no máximo 50 ganhadores por vez.");
    const pr = lerPremio();
    if (pr.erro) return modal.error(pr.erro);
    const val = lerValidade();
    if (pr.valor != null && val.erro) return modal.error(val.erro);
    const total = participantes?.total || 0;
    if (!total) return modal.error(modo === "dias" ? "Nenhum cliente abasteceu nos dias marcados." : "Nenhum cliente abasteceu neste período.");
    if (qtd > total) return modal.error(`Só há ${total} participante(s) para ${qtd} ganhador(es).`);

    const quando = modo === "dias"
      ? `abasteceram em qualquer um dos ${diasOk.length} dia${diasOk.length > 1 ? "s" : ""} marcados`
      : `abasteceram de ${fmtData(isoIni)} a ${fmtData(isoFim)}`;
    const ok = await modal.confirm({
      title: "Realizar sorteio",
      message: `Sortear ${qtd} ganhador${qtd > 1 ? "es" : ""} entre ${total} cliente${total > 1 ? "s" : ""} que ${quando}${unitNome ? ` na unidade ${unitNome}` : " (todas as unidades)"}? O resultado fica registrado no histórico.`,
      details: [
        modo === "dias"
          ? { label: "Dias marcados", value: resumoDias(diasOk, 31) }
          : { label: "Período", value: `${fmtData(isoIni)} a ${fmtData(isoFim)}` },
        { label: "Ganhadores", value: String(qtd) },
        {
          label: "Prêmio",
          value: pr.valor != null
            ? `${fmtCent(pr.valor)} centavos por litro, uso único, válido por ${val.valor} dia${val.valor > 1 ? "s" : ""} em qualquer unidade`
            : "Sem prêmio na pista (só o resultado)",
        },
      ],
      confirmText: "Sortear",
    });
    if (!ok) return;
    setSorteando(true);
    try {
      const body = {
        modo, quantidade: qtd, unit_id: unitId !== "all" ? unitId : null,
        premio_centavos_litro: pr.valor,
        premio_validade_dias: val.valor || VALIDADE_PADRAO,
      };
      if (modo === "dias") body.dias = diasOk;
      else { body.inicio = isoIni; body.fim = isoFim; }
      const { data } = await masterApi.post("/master/sorteios", body);
      setResultado(data);
      loadHistorico();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSorteando(false); }
  };

  // Troca o prêmio (devolvido pelo cancelar) no resultado e no histórico, sem esperar o recarregar.
  const aplicarPremio = (novo) => {
    if (!novo?.id) return;
    const troca = (s) => (s ? {
      ...s,
      ganhadores: (s.ganhadores || []).map((g) => (g.premio?.id === novo.id ? { ...g, premio: { ...g.premio, ...novo } } : g)),
    } : s);
    setHistorico((h) => h.map(troca));
    setResultado((r) => troca(r));
  };

  const cancelarPremio = async (s, g, p) => {
    const centavos = s?.premio_centavos_litro ?? p.centavos_litro;
    const ok = await modal.confirm({
      title: "Cancelar prêmio do sorteio",
      message: `O prêmio de ${g.nome || "este ganhador"} será CANCELADO e deixará de aparecer na lista de descontos da pista. Esta ação não pode ser desfeita.`,
      details: [
        { label: "Ganhador", value: `${g.nome || "—"}${g.placa ? ` · ${g.placa}` : ""}` },
        { label: "Prêmio", value: centavos != null ? `${fmtCent(centavos)} centavos por litro` : "—" },
        { label: "Válido até", value: fmtData(p.expira_em) },
        { label: "Sorteio", value: `${fmtDataHora(s?.realizado_em)} · ${descricaoPeriodo(s)}` },
      ],
      confirmText: "Cancelar prêmio",
      cancelText: "Voltar",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(p.id);
    try {
      const { data } = await masterApi.post(`/master/sorteios/premios/${p.id}/cancelar`, {});
      aplicarPremio(data?.premio || { id: p.id, status: "cancelado", cancelado_em: new Date().toISOString() });
      modal.success({ title: "Prêmio cancelado", message: `O prêmio de ${g.nome || "este ganhador"} foi cancelado.` });
      loadHistorico();
    } catch (e) { modal.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const premioCell = (s, g, compacto) => {
    const p = premioDe(s, g);
    if (!p) return null;
    const st = STATUS_PREMIO[p.status] || { label: p.status, cls: "chip-gray" };
    const sub = p.status === "disponivel" ? `vale até ${fmtData(p.expira_em)}`
      : p.status === "usado" ? `usado em ${fmtDataHora(p.usado_em)}`
      : p.status === "cancelado" ? `cancelado em ${fmtDataHora(p.cancelado_em)}`
      : p.status === "expirado" ? `venceu em ${fmtData(p.expira_em)}` : "";
    return (
      <div className={`flex items-center gap-1.5 ${compacto ? "flex-wrap" : "justify-start sm:justify-end flex-wrap"}`}>
        <span data-testid={`premio-status-${p.id}`} className={`chip ${st.cls} text-[10px] py-0.5 uppercase tracking-wide font-semibold`}>
          {st.label}
        </span>
        {sub && <span className="text-[10.5px] text-neutral-500">{sub}</span>}
        {p.status === "disponivel" && (
          <button data-testid={`premio-cancelar-${p.id}`} disabled={busy === p.id}
                  onClick={() => cancelarPremio(s, g, p)}
                  className="btn-ghost h-7 px-2 text-[10px] font-semibold uppercase tracking-wide text-rose-700 inline-flex items-center gap-1 disabled:opacity-40">
            <XCircle size={11} /> Cancelar prêmio
          </button>
        )}
      </div>
    );
  };

  const tabCls = (ativo) => `px-3.5 h-8 rounded-full text-[11.5px] font-medium whitespace-nowrap transition-colors ${ativo ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`;
  const temPremio = String(premio).trim() !== "";

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-sorteio">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">Gestão</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Sorteio</h1>
        <p className="text-[12px] text-neutral-500 mt-1">
          Sorteio entre os clientes que abasteceram no período ou nos dias marcados. Cada cliente participa com 1 chance.
        </p>
      </div>

      <div className="card-soft rounded-xl p-4 space-y-3" data-testid="sorteio-form">
        <div className="flex gap-1 p-1 bg-white/80 border border-neutral-200/70 rounded-full w-fit max-w-full overflow-x-auto" data-testid="sorteio-modo">
          <button data-testid="sorteio-modo-periodo" onClick={() => setModo("periodo")} className={tabCls(modo === "periodo")}>Período</button>
          <button data-testid="sorteio-modo-dias" onClick={() => setModo("dias")} className={tabCls(modo === "dias")}>Dias específicos</button>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          {modo === "periodo" && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500">Abasteceram de</label>
                <input type="date" data-testid="sorteio-inicio" value={inicio} onChange={(e) => setInicio(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500">até</label>
                <input type="date" data-testid="sorteio-fim" value={fim} onChange={(e) => setFim(e.target.value)} className={inputCls} />
              </div>
            </>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Ganhadores</label>
            <input type="number" min={1} max={50} data-testid="sorteio-qtd" value={quantidade}
                   onChange={(e) => setQuantidade(e.target.value)} className={inputCls + " w-24"} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Prêmio (centavos por litro)</label>
            <input type="number" min={0} step="0.01" data-testid="sorteio-premio" value={premio} placeholder="Sem prêmio"
                   onChange={(e) => setPremio(e.target.value)} className={inputCls + " w-40 block"} />
          </div>
          <div style={{ opacity: temPremio ? 1 : 0.45 }}>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Validade do prêmio (dias)</label>
            <input type="number" min={1} max={VALIDADE_MAX} data-testid="sorteio-validade" value={validade} disabled={!temPremio}
                   onChange={(e) => setValidade(e.target.value)} className={inputCls + " w-32 block"} />
          </div>
          <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg text-[12px] font-medium"
               style={{ background: "rgba(10,42,115,0.08)", color: "var(--brand-blue)" }} data-testid="sorteio-unidade">
            <Building2 size={14} /> {unitNome || "Todas as unidades"}
          </div>
        </div>

        {modo === "dias" && (
          <SeletorDias dias={diasOk} onChange={setDias} mesInicial={mesDoDia(hoje)} />
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-[12.5px] text-neutral-700 flex items-center gap-1.5" data-testid="sorteio-participantes">
            <Users size={14} className="text-[color:var(--brand-blue)]" />
            {!selecaoValida ? (modo === "dias" ? "Marque os dias do sorteio." : "Informe um período válido.")
              : participantes == null ? "Contando participantes..."
              : <span><b>{participantes.total}</b> cliente{participantes.total === 1 ? "" : "s"} participa{participantes.total === 1 ? "" : "m"} ({participantes.abastecimentos} abastecimento{participantes.abastecimentos === 1 ? "" : "s"} {modo === "dias" ? "nos dias marcados" : "no período"})</span>}
          </div>
          <button data-testid="sorteio-btn" onClick={sortear} disabled={sorteando || !participantes?.total}
                  className="btn-action btn-novo">
            <Ticket size={15} /> {sorteando ? "Sorteando..." : "Realizar sorteio"}
          </button>
        </div>
        <p className="text-[11px] text-neutral-500">
          A unidade segue o seletor do topo da tela. Troca de óleo não conta como abastecimento.
          {" "}Com prêmio, cada ganhador recebe o desconto em centavos por litro para usar uma vez na pista, em qualquer unidade, dentro da validade.
        </p>
      </div>

      {resultado && (
        <div className="rounded-xl p-4 border border-amber-300 shadow-sm" data-testid="sorteio-resultado"
             style={{ background: "linear-gradient(135deg,#FFFBEB 0%,#FEF3C7 100%)" }}>
          <div className="flex items-center gap-2 mb-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Trophy size={17} className="text-white" />
            </div>
            <div>
              <div className="font-serif-display text-lg leading-tight text-amber-900">
                Resultado do sorteio
              </div>
              <div className="text-[11px] text-amber-800">
                {fmtDataHora(resultado.realizado_em)} · {resultado.total_participantes} participantes · {resultado.modo === "dias" ? "" : "período "}{descricaoPeriodo(resultado)}
              </div>
              <div className="text-[11px] text-amber-800" data-testid="sorteio-resultado-premio">
                {resultado.premio_centavos_litro
                  ? `Prêmio: ${fmtCent(resultado.premio_centavos_litro)} centavos por litro · validade ${resultado.premio_validade_dias || VALIDADE_PADRAO} dias`
                  : "Sorteio sem prêmio na pista"}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {(resultado.ganhadores || []).map((g) => (
              <div key={g.cliente_id} className="flex flex-wrap items-center justify-between gap-2 bg-white/80 rounded-lg px-3 py-2 border border-amber-200"
                   data-testid={`sorteio-ganhador-${g.posicao}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-7 h-7 rounded-full bg-amber-500 text-white text-[12px] font-bold flex items-center justify-center shrink-0">{g.posicao}º</span>
                  <div className="min-w-0">
                    <div className="font-semibold text-[13px] truncate">{g.nome || "—"}</div>
                    <div className="text-[11px] text-neutral-600">{g.placa || "—"} · {formatPhone(g.telefone)}</div>
                  </div>
                </div>
                <div className="flex flex-col items-start sm:items-end gap-1 min-w-0 max-w-full sm:shrink-0">
                  {g.abastecimentos_no_periodo != null && (
                    <div className="text-[11px] text-neutral-600">{g.abastecimentos_no_periodo} abast. {resultado.modo === "dias" ? "nos dias" : "no período"}</div>
                  )}
                  {premioCell(resultado, g, false)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <section className="card-soft rounded-xl p-4">
        <h2 className="font-serif-display text-lg mb-2.5">Sorteios realizados</h2>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Realizado em</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Período / dias</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Participantes</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Prêmio</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Ganhador(es)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {historico.map((s) => (
                <TableRow key={s.id} data-testid={`sorteio-hist-${s.id}`}>
                  <TableCell className="text-[12px] whitespace-nowrap">{fmtDataHora(s.realizado_em)}</TableCell>
                  <TableCell className="text-[12px]">
                    {s.modo === "dias" && Array.isArray(s.dias) ? (
                      <div title={(s.dias || []).map(fmtDiaCurto).join(", ")} data-testid={`sorteio-hist-dias-${s.id}`}>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Dias específicos</div>
                        <div>{resumoDias(s.dias)}</div>
                      </div>
                    ) : (
                      <span className="whitespace-nowrap">{fmtData(s.inicio)} a {fmtData(s.fim)}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-[12px]">{s.unit_nome || "Todas"}</TableCell>
                  <TableCell className="text-[12px]">{s.total_participantes}</TableCell>
                  <TableCell className="text-[12px] whitespace-nowrap" data-testid={`sorteio-hist-premio-${s.id}`}>
                    {s.premio_centavos_litro
                      ? <>{fmtCent(s.premio_centavos_litro)} ¢/L<div className="text-[10.5px] text-neutral-500">{s.premio_validade_dias || VALIDADE_PADRAO} dias</div></>
                      : <span className="text-neutral-400">Sem prêmio</span>}
                  </TableCell>
                  <TableCell className="text-[12px]">
                    <div className="space-y-1">
                      {(s.ganhadores || []).map((g) => (
                        <div key={g.cliente_id}>
                          <div>{g.posicao ? `${g.posicao}º ` : ""}{g.nome} <span className="text-neutral-500">· {g.placa}</span></div>
                          {premioCell(s, g, true)}
                        </div>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {historico.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-6">Nenhum sorteio realizado ainda.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}

/**
 * Seletor de dias soltos (item 3): grade do mês com clique para marcar/desmarcar e atalho
 * "todos os <dia da semana> de <mês>" (usa o mês mostrado na grade). Limite de 31 dias.
 */
function SeletorDias({ dias, onChange, mesInicial }) {
  const [mes, setMes] = useState(mesInicial);
  const [diaSemana, setDiaSemana] = useState(6); // sábado: o exemplo do pedido
  const nomeDS = DIAS_SEMANA.find((d) => d.valor === Number(diaSemana)) || DIAS_SEMANA[6];

  const alternar = (dia) => {
    const r = alternarDia(dias, dia);
    if (r.erro) return modal.error(r.erro);
    onChange(r.dias);
  };
  const atalho = () => {
    const novos = diasDaSemanaNoMes(mes, diaSemana);
    const r = unirDias(dias, novos);
    if (r.erro) return modal.error(r.erro);
    onChange(r.dias);
  };

  return (
    <div className="rounded-lg border border-neutral-200 bg-white/70 p-3 space-y-3" data-testid="sorteio-dias">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Atalho: dia da semana</label>
          <select data-testid="sorteio-atalho-dia" value={diaSemana} onChange={(e) => setDiaSemana(Number(e.target.value))}
                  className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block">
            {DIAS_SEMANA.map((d) => <option key={d.valor} value={d.valor}>{d.plural}</option>)}
          </select>
        </div>
        <button data-testid="sorteio-atalho-btn" onClick={atalho}
                className="btn-ghost h-9 px-3 text-[11px] font-medium inline-flex items-center gap-1.5">
          <CalendarDays size={13} /> Marcar {nomeDS.todos} {nomeDS.plural} de {nomeMes(mes)}
        </button>
        {dias.length > 0 && (
          <button data-testid="sorteio-dias-limpar" onClick={() => onChange([])}
                  className="btn-ghost h-9 px-3 text-[11px] font-medium">Limpar dias</button>
        )}
        <div className="text-[11px] text-neutral-500 ml-auto" data-testid="sorteio-dias-contador">
          {dias.length} de {LIMITE_DIAS} dias marcados
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-[auto_1fr] items-start">
        <div className="w-full max-w-[300px]">
          <div className="flex items-center justify-between mb-1.5">
            <button data-testid="sorteio-mes-anterior" onClick={() => setMes(somaMes(mes, -1))} title="Mês anterior"
                    className="icon-action"><ChevronLeft size={15} /></button>
            <div className="text-[12.5px] font-semibold capitalize" data-testid="sorteio-mes">{nomeMes(mes)}</div>
            <button data-testid="sorteio-mes-proximo" onClick={() => setMes(somaMes(mes, 1))} title="Próximo mês"
                    className="icon-action"><ChevronRight size={15} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {DIAS_SEMANA.map((d) => (
              <div key={d.valor} className="text-[9.5px] uppercase tracking-wider text-neutral-400">{d.curto}</div>
            ))}
            {gradeDoMes(mes).flat().map((dia, i) => (
              dia ? (
                <button key={dia} data-testid={`sorteio-dia-${dia}`} onClick={() => alternar(dia)}
                        aria-pressed={dias.includes(dia)}
                        className={`h-8 rounded-md text-[12px] font-medium transition-colors border ${dias.includes(dia)
                          ? "text-white border-transparent"
                          : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-300"}`}
                        style={dias.includes(dia) ? { background: "linear-gradient(135deg,#1D4ED8 0%,#2563EB 100%)" } : undefined}>
                  {Number(dia.slice(8, 10))}
                </button>
              ) : <div key={`v${i}`} />
            ))}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Dias marcados</div>
          {dias.length === 0 ? (
            <div className="text-[11.5px] text-neutral-500">Nenhum dia marcado. Clique nos dias da grade ou use o atalho.</div>
          ) : (
            <div className="flex flex-wrap gap-1" data-testid="sorteio-dias-lista">
              {dias.map((d) => (
                <span key={d} className="chip text-[10.5px] py-0.5 pr-1.5">
                  {fmtDiaCurto(d)}
                  <button data-testid={`sorteio-dia-remover-${d}`} onClick={() => alternar(d)} title="Remover dia"
                          className="inline-flex items-center justify-center w-4 h-4 rounded hover:bg-black/10"><X size={10} /></button>
                </span>
              ))}
            </div>
          )}
          <p className="text-[10.5px] text-neutral-500 mt-2">
            Participa quem abasteceu em qualquer um dos dias marcados (dia em Brasília), com 1 chance por cliente.
          </p>
        </div>
      </div>
    </div>
  );
}
